import { AllSecuritiesResponseProto, Security, SecurityCategory_CategoryCode, Segment } from '../../proto/generated/security'
import { futureInstrumentFor } from './optionSynth'
import { readSecuritiesMeta, readSecuritiesSnapshot, writeSecuritiesSnapshot } from './securitiesDb'
import type { OptionSeriesDef, RevalidateOutcome, RevalidateRequest, SecuritiesMeta, SecuritiesSnapshot, StoredSecurities, SyncTimings } from './types'
import { optionSeriesId } from './types'

/**
 * Flattens the securities master into what we keep in memory: one `Security` per cash,
 * derived and futures instrument, plus one compact `OptionSeriesDef` per option series.
 * Option securities themselves are synthesised on demand (see optionSynth.ts).
 */
export function buildSnapshot(response: AllSecuritiesResponseProto): SecuritiesSnapshot {
    const securities: Security[] = []
    const optionSeries: OptionSeriesDef[] = []

    response.exchangeSecurities.forEach((exchangeSecurityList) => {
        const exchange = exchangeSecurityList.exchange
        exchangeSecurityList.cashSecurities.forEach((cashSecurity) => {
            const { foData, ...cs } = cashSecurity
            securities.push({
                ...cs,
                exchange,
                derived: false,
                underlyingSecurityCode: cs.code,
                segment: Segment.CM,
                exchangeSymbol: cs.exchangeSymbol,
                partOfCAS: cs.partOfCAS,
                tradeable: cs.tradeable,
                isPrimary: cs.isPrimary,
                isin: cs.isin,
            })
            if (!foData) return
            const futureInstrument = futureInstrumentFor(cs.categoryCode)
            foData.derivedSecurities.forEach((derivedSecurity) => {
                securities.push({
                    ...cs,
                    ...derivedSecurity,
                    exchange,
                    hasFo: false,
                    segment: Segment.FO,
                    derived: true,
                    underlyingSecurityCode: cs.code,
                    series: 'XX',
                    partOfCAS: false,
                    tradeable: true,
                })
            })
            foData.futuresSecurities.forEach((futureSecurity) => {
                securities.push({
                    ...cs,
                    ...futureSecurity,
                    segment: Segment.FO,
                    series: 'XX',
                    exchange,
                    hasFo: false,
                    derived: false,
                    categoryCode: SecurityCategory_CategoryCode.FUTURES,
                    underlyingSecurityCode: cs.code,
                    instrument: futureInstrument,
                    exchangeSymbol: cs.exchangeSymbol,
                    name: futureSecurity.name,
                    partOfCAS: false,
                    tradeable: true,
                })
            })
            foData.optionsSecurities.forEach((optionSecurity) => {
                optionSeries.push({
                    id: optionSeriesId(exchange, optionSecurity.securityCodePrefix ?? ''),
                    exchange,
                    underlyingCode: cs.code,
                    securityCodePrefix: optionSecurity.securityCodePrefix ?? '',
                    symbolPrefix: optionSecurity.symbolPrefix ?? '',
                    displayNamePrefix: optionSecurity.displayNamePrefix ?? '',
                    priceMultiplier: optionSecurity.priceMultiplier,
                    lotSize: optionSecurity.lotSize,
                    volumeFreezeQuantity: optionSecurity.volumeFreezeQuantity,
                    tickSize: optionSecurity.tickSize,
                    expiry: optionSecurity.expiry,
                    name: optionSecurity.name,
                    strikes: optionSecurity.strikes,
                })
            })
        })
    })

    return { securities, optionSeries }
}

async function fetchSecuritiesMaster(req: RevalidateRequest, validator: Record<string, string> | null): Promise<Response> {
    const headers: Record<string, string> = {
        Accept: 'application/x-protobuf',
        ...(req.token ? { Authorization: `Bearer ${req.token}` } : {}),
        ...(validator ?? {}),
    }
    // IndexedDB is our cache; keep the browser from storing a second 3.4MB copy of the body.
    return fetch(`${req.baseUrl}/v2/security/all`, { method: 'GET', headers, cache: 'no-store', credentials: 'omit' })
}

const validatorOf = (meta: SecuritiesMeta | null) => meta?.etag ?? meta?.lastModified ?? null

/** Records how long each named phase took, in ms. */
class PhaseTimer {
    readonly timings: SyncTimings = {}
    private mark = performance.now()
    lap(name: string) {
        const now = performance.now()
        this.timings[name] = Math.round(now - this.mark)
        this.mark = now
    }
}

/**
 * Stale-while-revalidate over IndexedDB. `SecuritiesSyncer.loadStored` returns whatever is
 * persisted so the app can start on it immediately; `revalidate` then asks the server with
 * the stored validator (ETag, else Last-Modified). A 304 confirms the stored copy; a 200 is
 * decoded, handed to `onSnapshot` straight away, then persisted so the next load gets a 304.
 * Network failures keep the stored copy in use.
 *
 * Runs unchanged inside the Web Worker or, as a fallback, on the main thread.
 */
export class SecuritiesSyncer {
    private meta: SecuritiesMeta | null = null
    private hasStoredSnapshot = false

    async loadStored(): Promise<StoredSecurities> {
        const timer = new PhaseTimer()
        this.meta = await readSecuritiesMeta()
        timer.lap('idbMeta')
        const snapshot = this.meta ? await readSecuritiesSnapshot() : null
        timer.lap('idbRead')
        this.hasStoredSnapshot = !!snapshot
        return { snapshot, validator: snapshot ? validatorOf(this.meta) : null, timings: timer.timings }
    }

    async revalidate(
        req: RevalidateRequest,
        onSnapshot: (snapshot: SecuritiesSnapshot, validator: string | null) => void,
    ): Promise<RevalidateOutcome> {
        const conditional = this.hasStoredSnapshot
            ? this.meta?.etag
                ? { 'If-None-Match': this.meta.etag }
                : this.meta?.lastModified
                  ? { 'If-Modified-Since': this.meta.lastModified }
                  : null
            : null

        const timer = new PhaseTimer()
        let response: Response | null = null
        try {
            response = await fetchSecuritiesMaster(req, conditional)
        } catch (error) {
            timer.lap('fetch')
            return { result: 'failed', message: error instanceof Error ? error.message : String(error), timings: timer.timings }
        }
        timer.lap('fetch')

        if (response.status === 304) {
            return { result: 'not-modified', timings: timer.timings }
        }
        if (!response.ok) {
            return { result: 'failed', message: `securities master request failed with HTTP ${response.status}`, timings: timer.timings }
        }

        const bytes = new Uint8Array(await response.arrayBuffer())
        timer.lap('download')
        const snapshot = buildSnapshot(AllSecuritiesResponseProto.decode(bytes))
        timer.lap('decode')
        const nextMeta: SecuritiesMeta = {
            etag: response.headers.get('etag'),
            lastModified: response.headers.get('last-modified'),
            savedAt: Date.now(),
            securityCount: snapshot.securities.length,
            optionSeriesCount: snapshot.optionSeries.length,
        }
        const validator = validatorOf(nextMeta)
        onSnapshot(snapshot, validator)
        // Without a validator the next load would have to download everything anyway.
        if (validator) {
            const persisted = await writeSecuritiesSnapshot(snapshot, nextMeta)
            timer.lap('idbWrite')
            if (persisted) {
                this.meta = nextMeta
                this.hasStoredSnapshot = true
            }
        }
        return { result: 'updated', validator, timings: timer.timings }
    }
}
